import ReactDOM from 'react-dom/client';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { exportPopulationData } from '../../../../../redux_store';
import ExportableChartWrapper from '../components/ExportableChartWrapper';

const useChartExport = (chartRef, currentAccountUuid, exportOptions = {}) => {
  const dispatch = useDispatch();

  // Loading states
  const [isPngExporting, setIsPngExporting] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isDataExporting, setIsDataExporting] = useState(false);

  // Helper to create temporary export wrapper with SVG
  const createExportWrapper = useCallback((svgElement, exportOptions) => {
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.top = '-9999px';
    tempDiv.style.left = '-9999px';
    tempDiv.style.width = '1250px';
    tempDiv.style.minHeight = '950';
    document.body.appendChild(tempDiv);

    // Clone the SVG to avoid modifying the original
    const clonedSvg = svgElement.cloneNode(true);

    // Ensure SVG has proper dimensions and styles
    clonedSvg.setAttribute('width', '100%');
    clonedSvg.setAttribute('height', '100%');
    clonedSvg.style.width = '100%';
    clonedSvg.style.height = '100%';

    // Add inline styles to ensure proper rendering
    const style = document.createElement('style');
    style.textContent = `
      .recharts-cartesian-axis-tick-value {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif !important;
        font-size: 11px !important;
        fill: #374151 !important;
        font-weight: 500 !important;
        letter-spacing: 0.01em !important;
      }
      .recharts-cartesian-axis-tick-line {
        stroke: #CBD5E1 !important;
        stroke-width: 1.5 !important;
      }
      .recharts-cartesian-axis-line {
        stroke: #CBD5E1 !important;
        stroke-width: 1 !important;
        stroke-opacity: 0.7 !important;
      }
      .recharts-legend-wrapper {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif !important;
      }
      .recharts-legend-item-text {
        font-size: 11px !important;
        fill: #374151 !important;
        font-weight: 600 !important;
      }
    `;
    clonedSvg.insertBefore(style, clonedSvg.firstChild);

    const ExportWrapper = () => (
      <ExportableChartWrapper
        title={exportOptions.title || "Population Health Assessment Report"}
        subtitle={exportOptions.subtitle || "Comprehensive patient outcome analysis and clinical metrics overview"}
        dateRange={exportOptions.dateRange}
        selectedInstrument={exportOptions.selectedInstrument}
        isComparisonMode={exportOptions.isComparisonMode}
        totalPatients={exportOptions.totalPatients}
        visible={true}
      >
        <div
          style={{
            width: 'auto',
            height: 600,
            paddingBottom: '100px'
          }}
          dangerouslySetInnerHTML={{ __html: clonedSvg.outerHTML }}
        />
      </ExportableChartWrapper>
    );

    const root = ReactDOM.createRoot(tempDiv);
    return new Promise((resolve) => {
      root.render(<ExportWrapper />);
      // Wait for render
      setTimeout(() => {
        resolve({ tempDiv, root });
      }, 100);
    });
  }, []);
  const handleDataExport = useCallback(async (filters, isComparisonMode) => {
    if (isDataExporting) return;

    setIsDataExporting(true);
    try {
      const accountUuid = currentAccountUuid || 'demo-account';
      const result = await dispatch(exportPopulationData({
        accountUuid,
        filters,
        isComparisonMode
      })).unwrap();

      // Result should have csvContent and filename directly now
      const blob = new Blob([result.csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', result.filename || 'population_health_export.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export data:', err);
    } finally {
      setIsDataExporting(false);
    }
  }, [dispatch, currentAccountUuid, isDataExporting]);

  const handleChartPngExport = useCallback(async (svgElement, baseFilename, exportOptions) => {
    if (isPngExporting) return;

    setIsPngExporting(true);

    try {
      // Create temporary wrapper with professional design
      const { tempDiv, root } = await createExportWrapper(svgElement, exportOptions);

      // Capture the wrapper with html2canvas
      const canvas = await html2canvas(tempDiv, {
        backgroundColor: '#fafbfc',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: 1250,
        height: 950, // Increased height to accommodate chart with X-axis labels
      });

      // Cleanup
      root.unmount();
      document.body.removeChild(tempDiv);

      // Download the image
      canvas.toBlob((blob) => {
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${baseFilename}.png`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png', 1.0);

    } catch (error) {
      console.error('Failed to export PNG:', error);
      throw error;
    } finally {
      setIsPngExporting(false);
    }
  }, [createExportWrapper, isPngExporting]);

  const handleChartPdfExport = useCallback(async (baseFilename, svgElement, exportOptions) => {
    if (isPdfExporting) return;

    setIsPdfExporting(true);

    try {
      // Create temporary wrapper with professional design
      const { tempDiv, root } = await createExportWrapper(svgElement, exportOptions);

      // Capture the wrapper with html2canvas
      const canvas = await html2canvas(tempDiv, {
        backgroundColor: '#fafbfc',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: 1250,
        height: 950, // Increased height to accommodate chart with X-axis labels
      });

      // Cleanup
      root.unmount();
      document.body.removeChild(tempDiv);

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Calculate dimensions to fit the professional layout
      const margin = 2;
      const availableWidth = pdfWidth - (margin * 2);
      const availableHeight = pdfHeight - (margin * 2);

      const imgWidth = canvas.width * 0.264583; // Convert pixels to mm
      const imgHeight = canvas.height * 0.264583;

      const scaleX = availableWidth / imgWidth;
      const scaleY = availableHeight / imgHeight;
      const scale = Math.min(scaleX, scaleY, 1);

      const finalWidth = imgWidth * scale;
      const finalHeight = imgHeight * scale;

      const x = (pdfWidth - finalWidth) / 2;
      const y = (pdfHeight - finalHeight) / 2;

      pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight);
      pdf.save(`${baseFilename}.pdf`);

    } catch (error) {
      console.error('Failed to export PDF:', error);
      throw error;
    } finally {
      setIsPdfExporting(false);
    }
  }, [createExportWrapper, isPdfExporting]);

  const handleChartExport = useCallback(async (format) => {
    if (!chartRef.current) {
      console.warn('Chart reference not available');
      return;
    }

    try {
      const chartContainer = chartRef.current.querySelector('.recharts-wrapper');
      if (!chartContainer) {
        console.warn('Chart container not found');
        return;
      }

      const svgElement = chartContainer.querySelector('svg');
      if (!svgElement) {
        console.warn('SVG element not found');
        return;
      }

      const timestamp = new Date().toISOString().split('T')[0];
      const baseFilename = `population_health_chart_${timestamp}`;

      if (format === 'png') {
        await handleChartPngExport(svgElement, baseFilename, exportOptions);
      } else if (format === 'pdf') {
        await handleChartPdfExport(baseFilename, svgElement, exportOptions);
      }
    } catch (err) {
      console.error('Failed to export chart:', err);
      alert('Failed to export chart. Please try again.');
    }
  }, [chartRef, handleChartPngExport, handleChartPdfExport, exportOptions]);

  return {
    handleDataExport,
    handleChartExport,
    isPngExporting,
    isPdfExporting,
    isDataExporting,
  };
};

export default useChartExport;
